/**
 * Slate and Redux transcript trees drift: speaker/split/merge/red-herring
 * edits live in Slate only. Length-based sync is therefore unsafe.
 *
 * - 409 recovery: replace the whole Slate tree from the converted store copy.
 * - REALTIME poller: append only the store tail that was just concatenated,
 *   using the store's length *before* the concat as the offset.
 *
 * Assign `editor.children` on replace/append (do not editor.apply per node):
 * apply is quadratic on long files, normalizes the server copy, and records
 * poller appends as undo batches. Restore the caret by snippet id after
 * replace; concat leaves existing paths alone.
 */

import { Node, Transforms } from "slate";
import { ReactEditor } from "slate-react";

export const TRANSCRIPT_FORCE_REPLACE = "transcript:forceReplace";
export const TRANSCRIPT_APPEND = "transcript:append";

export function eventTargetsCurrentWorkflow(eventWorkflowId, currentWorkflowId) {
  if (eventWorkflowId == null || eventWorkflowId === "") return false;
  if (currentWorkflowId == null || currentWorkflowId === "") return false;
  return String(eventWorkflowId) === String(currentWorkflowId);
}

export function emitTranscriptForceReplace(transcripts, workflowId) {
  window.dispatchEvent(
    new CustomEvent(TRANSCRIPT_FORCE_REPLACE, {
      detail: { transcripts, workflowId },
    })
  );
}

export function emitTranscriptAppend(snippets, workflowId) {
  window.dispatchEvent(
    new CustomEvent(TRANSCRIPT_APPEND, {
      detail: { snippets, workflowId },
    })
  );
}

export function storeAppendedTail(storeTranscripts, storeLengthBeforeAppend) {
  if (!Array.isArray(storeTranscripts)) return [];
  const offset = Math.max(0, Number(storeLengthBeforeAppend) || 0);
  return storeTranscripts.slice(offset);
}

function wordsOf(snippet) {
  return snippet?.children?.[0]?.children;
}

export function selectionAtSnippet(children, snippetId, wordIndex = 0, offset = 0) {
  if (!Array.isArray(children) || children.length === 0 || snippetId == null) {
    return null;
  }

  const index = children.findIndex((n) => n?.id === snippetId);
  if (index < 0) return null;

  const words = wordsOf(children[index]);
  if (!Array.isArray(words) || words.length === 0) {
    const point = { path: [index, 0, 0], offset: 0 };
    return { anchor: point, focus: point };
  }

  const wordIdx = Math.min(Math.max(0, wordIndex), words.length - 1);
  const maxOffset = (words[wordIdx]?.text ?? "").length;
  const nextOffset = Math.min(Math.max(0, offset), maxOffset);
  const point = { path: [index, 0, wordIdx], offset: nextOffset };
  return { anchor: point, focus: point };
}

export function restoreSelectionAfterReplace(selection, prevChildren, nextChildren) {
  const anchor = selection?.anchor;
  if (!anchor) return null;

  const prevIdx = anchor.path?.[0];
  const snippetId = prevChildren?.[prevIdx]?.id;
  return selectionAtSnippet(
    nextChildren,
    snippetId,
    anchor.path?.[2] ?? 0,
    anchor.offset ?? 0
  );
}

function clearHistory(editor) {
  if (editor.history) {
    editor.history.undos = [];
    editor.history.redos = [];
  }
}

const DOM_POINT_OPTS = { exactMatch: false, suppressThrow: true };

function clampPoint(editor, point) {
  if (!point?.path) return null;
  try {
    const node = Node.get(editor, point.path);
    const maxOffset = typeof node?.text === "string" ? node.text.length : 0;
    return {
      path: point.path,
      offset: Math.min(Math.max(0, point.offset ?? 0), maxOffset),
    };
  } catch {
    return null;
  }
}

function clampRange(editor, range) {
  const anchor = clampPoint(editor, range?.anchor);
  if (!anchor) return null;
  const focus = clampPoint(editor, range?.focus) || anchor;
  return { anchor, focus };
}

/**
 * Map the native caret through Slate's DOM weakmap. Do not use data-index:
 * that attribute is word.id, assigned at load and left stale after deletes.
 */
export function selectionFromNativeDom(editor) {
  if (typeof window === "undefined" || !editor) return null;
  if (typeof ReactEditor.toSlateRange !== "function") return null;

  const native = window.getSelection?.();
  if (!native || native.rangeCount === 0 || !native.anchorNode) return null;

  let mapped = null;

  try {
    mapped = ReactEditor.toSlateRange(editor, native.getRangeAt(0), DOM_POINT_OPTS);
  } catch {
    mapped = null;
  }

  if (!mapped) {
    try {
      mapped = ReactEditor.toSlateRange(editor, native, DOM_POINT_OPTS);
    } catch {
      mapped = null;
    }
  }

  if (!mapped) {
    try {
      const point = ReactEditor.toSlatePoint(
        editor,
        [native.anchorNode, native.anchorOffset],
        DOM_POINT_OPTS
      );
      mapped = point ? { anchor: point, focus: point } : null;
    } catch {
      mapped = null;
    }
  }

  return clampRange(editor, mapped);
}

export function shouldRemapClickSelection(event) {
  if (!event || event.detail !== 1 || event.shiftKey) return false;
  if (typeof window === "undefined") return false;
  const native = window.getSelection?.();
  return Boolean(native && native.rangeCount > 0 && native.isCollapsed);
}

export function selectAtWordElement(editor, wordEl) {
  if (!editor || !wordEl) return false;

  const next = selectionFromNativeDom(editor);
  if (!next) return false;

  if (typeof editor.apply === "function") {
    Transforms.select(editor, next);
  } else {
    editor.selection = next;
  }
  return true;
}

export function applyTranscriptForceReplace(editor, transcripts) {
  if (!editor || !Array.isArray(transcripts) || transcripts.length === 0) {
    return;
  }

  const next = JSON.parse(JSON.stringify(transcripts));
  const nextSelection = restoreSelectionAfterReplace(
    editor.selection,
    editor.children,
    next
  );

  editor.children = next;
  editor.selection = nextSelection;
  clearHistory(editor);
  editor.onChange();
}

export function applyTranscriptAppend(editor, snippets) {
  if (!editor || !Array.isArray(snippets) || snippets.length === 0) {
    return;
  }

  editor.children = editor.children.concat(JSON.parse(JSON.stringify(snippets)));
  editor.onChange();
}
